"use client";

import React from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { useIsMobileOrTab } from "@/hooks/use-mobile-tab";
import { getImageUrl } from "@/utils/utils";

interface AboutPageCertificationProps {
  aboutData: any;
  lang: "en" | "jp";
}

export default function AboutPageCertification({
  aboutData,
  lang,
}: AboutPageCertificationProps) {
  const [selectedCertificate, setSelectedCertificate] = React.useState<
    string | null
  >(null);
  const [isCertificateModalOpen, setIsCertificateModalOpen] =
    React.useState(false);

  const isMobileOrTab = useIsMobileOrTab();

  const openCertificateModal = (certificateimage: any) => {
    setSelectedCertificate(certificateimage);
    setIsCertificateModalOpen(true);
  };

  const closeCertificateModal = () => {
    setIsCertificateModalOpen(false);
    setSelectedCertificate(null);
  };

  // Certificate Modal Component
  const CertificateModal = () => {
    if (!isCertificateModalOpen || !selectedCertificate) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <motion.div
          className="relative bg-white rounded-2xl p-4 max-w-[90vw] max-h-[90vh] overflow-y-auto"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3 }}
        >
          <button
            onClick={closeCertificateModal}
            className="absolute top-4 right-4 text-white transition-colors p-2 bg-primary-800 shadow-lg z-10 cursor-pointer"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <div className="relative">
            {selectedCertificate && (
              <Image
                src={getImageUrl(selectedCertificate || "")}
                alt="Certificate"
                width={1200}
                height={800}
                className="object-contain w-full h-auto"
              />
            )}
          </div>
        </motion.div>
      </div>
    );
  };

  return (
    <>
      {/* Certifications Section */}
      <motion.section
        className="bg-gray-25 flex items-center justify-center py-16 lg:py-[100px] px-4 md:px-6"
        initial={isMobileOrTab ? false : "hidden"}
        animate={isMobileOrTab ? "visible" : undefined}
        whileInView={isMobileOrTab ? undefined : "visible"}
        viewport={isMobileOrTab ? { once: true } : { once: true, amount: 0.3 }}
        variants={
          {
            hidden: { opacity: 0, y: 60 },
            visible: {
              opacity: 1,
              y: 0,
              transition: { duration: 0.8, ease: "easeOut" },
            },
          } as const
        }
      >
        <div className="w-full max-w-[1317px]">
          <div className="flex flex-col items-center gap-12 lg:gap-[50px]">
            {/* Section Header */}
            <div className="flex flex-col items-center gap-4 lg:gap-[17px]">
              <h2
                id="certification"
                className="scroll-mt-[80px] md:scroll-mt-[96px] lg:scroll-mt-[112px] text-[#0A1119] text-center font-inter text-3xl lg:text-[40px] font-medium leading-tight lg:leading-[58px] tracking-[-1.44px]"
              >
                {aboutData?.certification?.title}
              </h2>
              <div className="w-[50px] h-[2px] bg-[#1A5852]"></div>
            </div>

            {/* Certifications Cards Container */}
            <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-8 w-full">
              {aboutData?.certification?.items?.map(
                (cert: any, idx: number) => (
                  <motion.div
                    key={cert?.name + idx}
                    className="w-full lg:w-[642px] flex flex-col group"
                    variants={
                      {
                        hidden: { opacity: 0, y: 60 },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0.8, ease: "easeOut" },
                        },
                      } as const
                    }
                  >
                    <div className="bg-white p-8 flex flex-col gap-2.5 lg:min-h-[1026px]">
                      <div className="flex flex-col gap-8">
                        {/* Certificate Image */}
                        <button
                          type="button"
                          onClick={() => openCertificateModal(cert?.image?.url)}
                          className="relative w-full h-[344px] border border-[#F4F5F6] rounded-[10px] overflow-hidden bg-gray-100 group cursor-pointer"
                        >
                          {cert?.image && (
                            <Image
                              src={getImageUrl(cert?.image?.url || "")}
                              alt={cert?.name}
                              fill
                              style={{
                                aspectRatio: "289/184",
                                objectFit: "cover",
                                objectPosition: "top",
                              }}
                              className="transition-transform duration-300"
                            />
                          )}
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                            <div
                              className="flex items-center justify-center w-14 h-14 rounded-full bg-white/90 hover:bg-white shadow-lg transition"
                              aria-label="Zoom certificate"
                            >
                              <svg
                                width="88"
                                height="88"
                                viewBox="0 0 88 88"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <rect
                                  width="88"
                                  height="88"
                                  rx="44"
                                  fill="white"
                                />
                                <path
                                  d="M41.1992 43.7002H46.1992"
                                  stroke="#606977"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M43.6992 46.2002V41.2002"
                                  stroke="#606977"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M43.5 53C48.7467 53 53 48.7467 53 43.5C53 38.2533 48.7467 34 43.5 34C38.2533 34 34 38.2533 34 43.5C34 48.7467 38.2533 53 43.5 53Z"
                                  stroke="#606977"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M54 54L52 52"
                                  stroke="#606977"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                          </div>
                        </button>

                        {/* Certificate Content */}
                        <div className="flex flex-col gap-4">
                          <h3 className="text-[#1A1D21] font-inter text-xl lg:text-[24px] font-medium leading-[34px] tracking-[-0.72px]">
                            {cert?.name}
                          </h3>
                          <div className="w-full h-[2px] bg-[#F4F5F6]"></div>
                          <p className="text-[#4F5662] font-inter text-base leading-[26px] text-justify whitespace-pre-line">
                            {cert?.description}
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* Progress Bar */}
                    <div className="flex h-[2px] bg-gray-25 relative">
                      <div className="w-0 h-[2px] bg-[#1C8579] absolute left-0 top-0 group-hover:w-full transition-all duration-500"></div>
                    </div>
                  </motion.div>
                )
              )}
            </div>
          </div>
        </div>
      </motion.section>

      {/* Certificate Modal */}
      <CertificateModal />
    </>
  );
}
